import { useCallback, useEffect, useRef, useState } from 'react';
import { Link } from 'react-router';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { CircleHelp } from 'lucide-react';
import { Botao } from './base';
import { BotaoLerEmVoz } from './leitura-voz';
import { Carregando, Erro } from './estados';
import { Drawer } from './sobreposicao';
import { SubtituloPagina } from './subtitulo-pagina';
import { Tour, dom } from './tour';
import type { PassoTour } from './tour-passos';
import { blocosDoTexto } from '../nucleo/ajuda-texto';
import { get, patch } from '../nucleo/api';
import { dicaPendente, passosDaDica } from '../nucleo/dicas';
import { useSessao } from '../nucleo/sessao';

/**
 * U15 F1 e F2: botão "Ajuda" do cabeçalho de cada tela (aba e Início). Abre o painel (o `Drawer` do sistema) com a aula de
 * ajuda da tela e, na primeira visita a cada tela, mostra as dicas em holofote (o tour do sistema com passos próprios).
 */

interface AulaAjuda {
  id: string;
  curso_id: string;
  curso_titulo: string;
  modulo_titulo: string;
  titulo: string;
  tipo: 'texto' | 'video';
  texto: string | null;
  duracao_min: number;
}

/** Rota da aula no Treinamento (a área de membros abre na aula pelo `?aula=`). */
export const rotaDaAula = (a: Pick<AulaAjuda, 'curso_id' | 'id'>) => `/treinamento/treinamento/curso/${a.curso_id}?aula=${a.id}`;

/** O texto da aula em blocos: subtítulo, parágrafos e passos numerados (lista ordenada). Sempre texto, nunca HTML. */
export function TextoDaAjuda({ texto }: { texto: string | null }) {
  const blocos = blocosDoTexto(texto);
  return (
    <div className="flex flex-col gap-4" data-ajuda-texto="">
      {blocos.map((b, i) => (
        <section key={i} className="flex flex-col gap-1.5">
          {b.titulo && <h4 className="text-sm font-semibold text-tinta-1">{b.titulo}</h4>}
          {b.paragrafos.map((p, j) => (
            <p key={j} className="text-sm text-tinta-2">
              {p}
            </p>
          ))}
          {b.passos.length > 0 && (
            <ol className="ml-5 flex list-decimal flex-col gap-1.5 text-sm text-tinta-2">
              {b.passos.map((p, j) => (
                <li key={j}>{p}</li>
              ))}
            </ol>
          )}
        </section>
      ))}
    </div>
  );
}

/** Conteúdo do painel: as aulas da tela, ou (sem aula ligada) o subtítulo, o caminho e o contato do administrador. */
export function ConteudoAjuda({ chave, caminho, aoNavegar }: { chave: string; caminho: string; aoNavegar: () => void }) {
  const { t, i18n } = useTranslation();
  const s = useSessao();
  // U15 F4: o texto lido em voz alta é o do próprio painel (aula, ou subtítulo e caminho).
  const texto = useRef<HTMLDivElement>(null);
  const ler = <BotaoLerEmVoz alvo={texto} idioma={i18n.language} rotuloLer={t('ajudaTela.lerEmVoz')} rotuloParar={t('ajudaTela.pararLeitura')} />;
  const q = useQuery({
    queryKey: ['treinamento', 'ajuda', chave],
    queryFn: () => get<{ tela: string; aulas: AulaAjuda[] }>(`/treinamento/ajuda/${encodeURIComponent(chave)}`),
    staleTime: 60_000,
  });
  const podeTreinamento = s.instalado('treinamento') && s.pode('treinamento.ver');
  if (q.isPending) return <Carregando texto={t('ajudaTela.carregando')} />;
  if (q.isError) return <Erro erro={q.error} aoTentar={() => void q.refetch()} />;
  const aulas = q.data.aulas;
  if (!aulas.length) {
    return (
      <div className="flex flex-col gap-3" data-ajuda-sem-aula="">
        <div className="self-start">{ler}</div>
        <div ref={texto} className="flex flex-col gap-3">
          <SubtituloPagina />
          <p className="text-sm text-tinta-2">
            <span className="font-semibold text-tinta-1">{t('ajudaTela.ondeFica')}: </span>
            {caminho}
          </p>
          <p className="text-sm text-tinta-2">{t('ajudaTela.falarAdmin')}</p>
        </div>
      </div>
    );
  }
  return (
    <div className="flex flex-col gap-4">
      <div className="self-start">{ler}</div>
      <div ref={texto} className="flex flex-col gap-6">
        {aulas.map((a) => (
          <article key={a.id} data-ajuda-aula={a.id} className="flex flex-col gap-3">
            <h3 className="text-base font-semibold">{a.titulo}</h3>
            <TextoDaAjuda texto={a.texto} />
            {podeTreinamento && (
              <Link to={rotaDaAula(a)} onClick={aoNavegar} className="text-sm font-medium text-acento-forte underline underline-offset-2">
                {a.tipo === 'video' ? t('ajudaTela.verVideo') : t('ajudaTela.abrirTreinamento')}
              </Link>
            )}
          </article>
        ))}
      </div>
    </div>
  );
}

/** Espera pelos alvos da tela (o conteúdo chega depois) e por nenhuma outra camada aberta antes de mostrar as dicas. */
const ATRASO_DICA_MS = 900;
const REPETE_DICA_MS = 500;
const TENTATIVAS_DICA = 6;

function DicasDaTela({ chave, aoConcluir }: { chave: string; aoConcluir: () => void }) {
  const { i18n } = useTranslation();
  const [passos, setPassos] = useState<PassoTour[] | null>(null);
  useEffect(() => {
    let vivo = true;
    let tentativa = 0;
    let id: ReturnType<typeof setTimeout>;
    const tentar = () => {
      if (!vivo) return;
      tentativa++;
      // Outra camada aberta (pop-up de novidades, formulário): as dicas esperam a vez.
      if (document.querySelector('[role="dialog"][aria-modal="true"]') && tentativa < TENTATIVAS_DICA * 2) {
        id = setTimeout(tentar, 1000);
        return;
      }
      const p = passosDaDica(chave, dom, (k) => i18n.exists(k));
      if (p.length < 2 && tentativa < TENTATIVAS_DICA) {
        id = setTimeout(tentar, REPETE_DICA_MS);
        return;
      }
      if (p.length) setPassos(p);
    };
    id = setTimeout(tentar, ATRASO_DICA_MS);
    return () => {
      vivo = false;
      clearTimeout(id);
    };
  }, [chave, i18n]);
  if (!passos) return null;
  return (
    <Tour
      passos={passos}
      menuAberto={false}
      definirMenu={() => undefined}
      aoConcluir={() => {
        setPassos(null);
        aoConcluir();
      }}
    />
  );
}

function AjudaDaTelaInterna({ chave, caminho }: { chave: string; caminho: string }) {
  const { t } = useTranslation();
  const s = useSessao();
  const [aberto, setAberto] = useState(false);
  // O tour do primeiro login estava concluído quando a tela abriu? Quem acaba de terminá-lo só vê as dicas na próxima visita.
  const onboardingAoAbrir = useRef(s.usuario.onboarding_concluido).current;
  // Telas marcadas neste aparelho agora (o servidor confirma em seguida) e "Rever as dicas" (mostra já, sem esperar a sessão).
  const [vistasAgora, setVistasAgora] = useState<ReadonlySet<string>>(() => new Set());
  const [forcar, setForcar] = useState(false);
  const recarregar = s.recarregar;
  const marcarVista = useCallback(() => {
    setForcar(false);
    setVistasAgora((v) => new Set(v).add(chave));
    // Sem aviso se falhar: a dica só aparece de novo na próxima visita.
    void patch('/conta/preferencias', { dica_vista: chave }).then(() => recarregar()).catch(() => undefined);
  }, [chave, recarregar]);
  const reverDicas = () => {
    setAberto(false);
    setVistasAgora((v) => {
      const n = new Set(v);
      n.delete(chave);
      return n;
    });
    setForcar(true);
    void patch('/conta/preferencias', { dica_rever: chave }).then(() => recarregar()).catch(() => undefined);
  };
  const mostrarDicas = forcar || dicaPendente({ tela: chave, onboardingConcluidoAoAbrir: onboardingAoAbrir, vistas: s.usuario.dicas_vistas, vistasAgora });
  return (
    <>
      <Botao variante="fantasma" tamanho="sm" data-tour="ajuda-tela" data-ajuda-botao="" icone={<CircleHelp className="size-4" aria-hidden />} onClick={() => setAberto(true)}>
        {t('ajudaTela.botao')}
      </Botao>
      <Drawer
        aberto={aberto}
        aoFechar={() => setAberto(false)}
        titulo={t('ajudaTela.titulo')}
        rodape={
          <Botao variante="secundario" tamanho="sm" data-ajuda-rever="" onClick={reverDicas}>
            {t('ajudaTela.reverDicas')}
          </Botao>
        }
      >
        <ConteudoAjuda chave={chave} caminho={caminho} aoNavegar={() => setAberto(false)} />
      </Drawer>
      {mostrarDicas && <DicasDaTela key={forcar ? 'rever' : 'primeira'} chave={chave} aoConcluir={marcarVista} />}
    </>
  );
}

/** O botão e o painel de uma tela; a chave nova (outra aba) recomeça o painel e as dicas. */
export function AjudaDaTela(props: { chave: string; caminho: string }) {
  return <AjudaDaTelaInterna key={props.chave} {...props} />;
}
